import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Truck, CalendarDays, Users, CreditCard, Settings, Plus, Menu, LogOut, History, FileText, Wallet, BarChart3, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/bookings", label: "Bookings", icon: Truck, id: "bookings" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, id: "calendar" },
  { to: "/customers", label: "Customers", icon: Users, id: "customers" },
  { to: "/pricing", label: "Pricing", icon: Calculator, id: "pricing" },
  { to: "/payments", label: "Payments", icon: CreditCard, id: "payments", admin: true },
  { to: "/invoices", label: "Invoices", icon: FileText, id: "invoices", admin: true },
  { to: "/payroll", label: "Payroll", icon: Wallet, id: "payroll", admin: true },
  { to: "/reports", label: "Reports", icon: BarChart3, id: "reports", admin: true },
  { to: "/settings", label: "Settings", icon: Settings, id: "settings", admin: true },
  { to: "/audit", label: "Audit log", icon: History, id: "audit", admin: true },
];

const NavItems = ({ onClick, mobile }) => {
  const { user } = useAuth();
  return NAV.filter((n) => !n.admin || ["admin", "owner"].includes(user?.role)).map(({ to, label, icon: Icon, id }) => (
    <NavLink
      key={to}
      to={to}
      end={to === "/"}
      onClick={onClick}
      data-testid={`nav-${id}-${mobile ? "mobile" : "tab"}`}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-1.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors",
          isActive ? "bg-white/10 text-white shadow-[inset_0_-2px_0_#FACC15]" : "text-slate-300 hover:bg-white/5 hover:text-white"
        )
      }
    >
      <Icon className="h-4 w-4" />
      {label}
    </NavLink>
  ));
};

const Brand = () => (
  <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="brand-logo">
    <div className="grid h-9 w-9 place-items-center rounded-md bg-[#FACC15] text-[#0E1E38]">
      <Truck className="h-5 w-5" strokeWidth={2.5} />
    </div>
    <div className="leading-none">
      <div className="font-display text-[15px] font-extrabold tracking-tight text-white">CITY REMOVALISTS</div>
      <div className="mt-1 text-[10px] font-medium tracking-[0.22em] text-sky-200/80">&amp; STORAGE · OPS</div>
    </div>
  </Link>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <SettingsProvider>
      <div className="min-h-screen bg-[#F4F7FB]">
        <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0E1E38]">
          <div className="mx-auto flex h-16 max-w-[1500px] items-center gap-4 px-4 lg:px-6">
            <Brand />
            <nav className="hidden items-center gap-1 lg:flex">
              <NavItems />
            </nav>
            <div className="ml-auto flex items-center gap-2">
              <Button
                onClick={() => navigate("/bookings/new")}
                data-testid="btn-new-booking"
                className="bg-[#FACC15] font-semibold text-[#0E1E38] hover:bg-[#EAB308]"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">New Booking</span>
              </Button>
              <div className="hidden items-center gap-2 pl-2 xl:flex">
                <div className="text-right leading-tight">
                  <div className="text-sm font-medium text-white" data-testid="current-user-name">{user?.name}</div>
                  <div className="text-[11px] capitalize text-slate-400">{user?.role}</div>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={logout} data-testid="btn-logout" className="hidden text-slate-300 hover:bg-white/10 hover:text-white lg:inline-flex" title="Log out">
                <LogOut className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setOpen(true)} data-testid="btn-mobile-menu" className="text-white hover:bg-white/10 lg:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="left" className="w-72 border-none bg-[#0E1E38] p-5 [&>button]:!bg-transparent [&>button]:text-white [&>button]:opacity-90">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <Brand />
            <nav className="mt-8 flex flex-col gap-1">
              <NavItems mobile onClick={() => setOpen(false)} />
            </nav>
            <div className="mt-8 border-t border-white/10 pt-4">
              <div className="text-sm text-white">{user?.name}</div>
              <div className="mb-3 text-xs text-slate-400">{user?.email}</div>
              <Button variant="ghost" onClick={logout} data-testid="btn-logout-mobile" className="w-full justify-start text-slate-300 hover:bg-white/10 hover:text-white">
                <LogOut className="h-4 w-4" /> Log out
              </Button>
            </div>
          </SheetContent>
        </Sheet>
        <main className="mx-auto max-w-[1500px] px-4 py-6 lg:px-8 lg:py-8">
          <Outlet />
        </main>
      </div>
    </SettingsProvider>
  );
}
